import { Card, CardContent } from "@/components/ui/card";
import { Users, ShieldCheck, Building2, Activity } from "lucide-react";
import { Admin } from "@/types/company";
import { cn } from "@/lib/utils";

interface UserStatsProps {
  users: Admin[];
}

export function UserStats({ users }: UserStatsProps) {
  const stats = {
    total: users.length,
    superAdmins: users.filter(user => user.role === 'super_admin').length,
    managers: users.filter(user => user.role === 'manager').length,
    activeCompanies: new Set(users.filter(user => user.company).map(user => user.company?._id)).size
  };

  const items = [
    {
      label: "Toplam Kullanıcı",
      value: stats.total,
      icon: Users,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      label: "Süper Admin",
      value: stats.superAdmins,
      icon: ShieldCheck,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
    {
      label: "Firma Yöneticisi",
      value: stats.managers,
      icon: Building2,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      label: "Aktif Firma",
      value: stats.activeCompanies,
      icon: Activity,
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map((item) => (
        <Card key={item.label} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
          <CardContent className="p-0">
            <div className="relative">
              {/* Arka plan dekoratif gradient */}
              <div className={`absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full ${item.bgColor} blur-2xl`}></div>

              <div className="p-6 relative">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{item.label}</p>
                    <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{item.value}</h3>
                  </div>
                  <div className={cn("p-3.5 rounded-xl shadow-sm", item.bgColor)}>
                    <item.icon className={cn("w-5 h-5", item.color)} strokeWidth={2.5} />
                  </div>
                </div>

                {/* Alt bilgi çubuğu */}
                <div className="pt-3 border-t border-slate-100">
                  {item.label === "Toplam Kullanıcı" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className="inline-block w-2 h-2 rounded-full mr-2 bg-blue-500"></span>
                        <span className="font-medium">Sistemde kayıtlı</span>
                      </div>
                      <span className="text-xs font-semibold text-blue-600">{item.value}</span>
                    </div>
                  )}

                  {item.label === "Süper Admin" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.value > 0 ? "bg-purple-500" : "bg-slate-300"}`}></span>
                        <span className="font-medium">
                          %{stats.total > 0 ? Math.round((item.value / stats.total) * 100) : 0} admin
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-purple-600">{item.value}/{stats.total}</span>
                    </div>
                  )}

                  {item.label === "Firma Yöneticisi" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.value > 0 ? "bg-green-500" : "bg-slate-300"}`}></span>
                        <span className="font-medium">
                          %{stats.total > 0 ? Math.round((item.value / stats.total) * 100) : 0} yönetici
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-green-600">{item.value}/{stats.total}</span>
                    </div>
                  )}

                  {item.label === "Aktif Firma" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className="inline-block w-2 h-2 rounded-full mr-2 bg-orange-500"></span>
                        <span className="font-medium">Bağlı firma sayısı</span>
                      </div>
                      <span className="text-xs font-semibold text-orange-600">{item.value}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
